import type { partProcess, partProcessRow } from "@/api/breakdownService";
import { isValidDecimalInput, parseDecimal } from "@/utils/inputParse";
import { TableRow, TableCell, TextField } from "@mui/material";
import { useEffect, useState } from "react";

interface PartProcessDataEditProps {
  row: partProcessRow;
  setFormData: React.Dispatch<React.SetStateAction<partProcess>>;
  type: "OEM" | "Spare";
  rowsTotal?: Array<{ id: number; total_price: number }>;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const PartProcessDataEdit = ({
  row,
  setFormData,
  type,
  rowsTotal,
  setFlaggedReason,
}: PartProcessDataEditProps) => {
  const [priceInput, setPriceInput] = useState<string>(
    Number(row.price_per_unit ?? 0).toFixed(2),
  );
  const [unitInput, setUnitInput] = useState<string>(
    Number(row.unit ?? 0).toFixed(2),
  );

  useEffect(() => {
    setFormData((prev) => {
      const foundRow =
        type === "OEM"
          ? prev.oem_part_process?.find((r) => r.id === row.id)
          : prev.spare_part_process?.find((r) => r.id === row.id);
      if (!foundRow) return prev;
      const updatedRow: partProcessRow = {
        ...foundRow,
        price_per_unit: parseDecimal(priceInput, 2) || 0,
        unit: parseDecimal(unitInput, 2) || 0,
      };
      return {
        ...prev,
        oem_part_process:
          type === "OEM"
            ? [
                ...(prev.oem_part_process?.filter((r) => r.id !== row.id) ??
                  []),
                updatedRow,
              ]
            : prev.oem_part_process,
        spare_part_process:
          type === "Spare"
            ? [
                ...(prev.spare_part_process?.filter((r) => r.id !== row.id) ??
                  []),
                updatedRow,
              ]
            : prev.spare_part_process,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceInput, unitInput, row.id, type]);

  return (
    <TableRow key={row.id} sx={{ backgroundColor: "#fff" }}>
      <TableCell
        sx={{
          width: "40%",
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {row.process_name}
      </TableCell>
      <TableCell
        sx={{
          width: "15%",
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <TextField
          value={row.detail}
          onChange={(e) => {
            const val = e.target.value;
            if (type === "OEM") {
              setFormData((prev) => {
                const foundRow = prev.oem_part_process?.find(
                  (r) => r.id === row.id,
                );
                if (!foundRow) return prev;
                const updatedRow: partProcessRow = {
                  ...foundRow,
                  detail: val,
                };
                return {
                  ...prev,
                  oem_part_process: [
                    ...(prev.oem_part_process?.filter((r) => r.id !== row.id) ??
                      []),
                    updatedRow,
                  ],
                };
              });
            } else {
              setFormData((prev) => {
                const foundRow = prev.spare_part_process?.find(
                  (r) => r.id === row.id,
                );
                if (!foundRow) return prev;
                const updatedRow: partProcessRow = {
                  ...foundRow,
                  detail: val,
                };
                return {
                  ...prev,
                  spare_part_process: [
                    ...(prev.spare_part_process?.filter(
                      (r) => r.id !== row.id,
                    ) ?? []),
                    updatedRow,
                  ],
                };
              });
            }
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "left" },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          width: "15%",
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <TextField
          value={unitInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setUnitInput(val.trim());
              if (type === "OEM") {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`OEM Part Process Value`),
                );
              } else {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`Spare Part Process Value`),
                );
              }
            }
          }}
          onBlur={() => {
            setUnitInput(parseDecimal(unitInput, 2).toFixed(2));
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "right" },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          width: "15%",
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <TextField
          value={priceInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setPriceInput(val.trim());
              if (type === "OEM") {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`OEM Part Process Value`),
                );
              } else {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`Spare Part Process Value`),
                );
              }
            }
          }}
          onBlur={() => {
            setPriceInput(parseDecimal(priceInput, 2).toFixed(2));
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "right" },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          width: "15%",
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {Number(rowsTotal?.find((r) => r.id === row.id)?.total_price ?? row.total_price ?? 0).toFixed(2)}
      </TableCell>
    </TableRow>
  );
};

export default PartProcessDataEdit;
